import React from 'react';
import { useTranslation } from 'next-i18next/pages';
import cn from 'clsx';
import { type Listing, type SearchListing } from '@pkgs/api';
import { Link } from '@ev-tech/ui-core-components/Link';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { HeartFilled } from '@/icons/small/HeartFilled';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useWatchlist } from '@/hooks/useWatchlist';

import styles from './FavoriteButton.module.css';

type FavoriteButtonProps = {
  className?: string;
  usesLinkComponent?: boolean;
  size?: 'small' | 'medium';

  listing: Listing | SearchListing;
};

export const FavoriteButton = ({ listing, className, usesLinkComponent, size = 'small' }: FavoriteButtonProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { handleWatchlistClick, getIsInWatchlist } = useWatchlist();
  const isInWatchlist = getIsInWatchlist(listing.id);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // prevent the click event from bubbling up to the parent element
    event.preventDefault();
    event.stopPropagation();
    handleWatchlistClick(listing);
  };

  if (usesLinkComponent) {
    return (
      <Link
        className={className}
        data-testid="expose-favorite-button"
        icon={<HeartFilled className={styles.heartIcon} data-in-watchlist={isInWatchlist} />}
        label={isInWatchlist ? t('search.watchlist.remove') : t('search.watchlist.add')}
        onClick={handleClick}
        as="button"
        size={size}
      />
    );
  }

  return (
    <IconButton
      className={cn(styles.button, className)}
      data-testid="expose-favorite-button"
      icon={<HeartFilled className={styles.heartIcon} data-in-watchlist={isInWatchlist} />}
      label={isInWatchlist ? t('search.watchlist.remove') : t('search.watchlist.add')}
      onClick={handleClick}
      size={size}
      variant="tertiary"
    />
  );
};
